import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../../../amplify/data/resource';

const client = generateClient<Schema>();

type AlertRule = {
  id: string;
  name: string;
  description?: string;
  ruleType?: string;
  severity?: string;
  enabled?: boolean;
  targetTenants?: string[];
  conditions?: Record<string, unknown>;
  actions?: Record<string, unknown>;
  createdBy?: string;
  updatedAt?: string;
};

type TenantRule = {
  ruleId?: string;
  ruleName?: string;
  ruleType?: string;
  severity?: string;
  triggeredAt?: string;
};

const SEVERITY_BADGE: Record<string, string> = {
  CRITICAL: 'bg-red-100 text-red-800',
  HIGH: 'bg-orange-100 text-orange-800',
  MEDIUM: 'bg-yellow-100 text-yellow-800',
  LOW: 'bg-blue-100 text-blue-800',
};

const RULE_TYPE_LABELS: Record<string, string> = {
  ACTIVITY_RULE: 'Activity Rule',
  DLP_RULE: 'DLP Rule',
  CUSTOM: 'Custom',
};

const DEFAULT_FORM = {
  name: '',
  description: '',
  ruleType: 'ACTIVITY_RULE',
  severity: 'MEDIUM',
  conditions: '{}',
  actions: '{}',
};

export default function RuleManagement() {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'standard' | 'tenant'>('standard');
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [formError, setFormError] = useState('');
  const [pushTargetRule, setPushTargetRule] = useState<AlertRule | null>(null);
  const [pushTenantIds, setPushTenantIds] = useState<string[]>([]);
  const [pushResult, setPushResult] = useState<any>(null);

  // Fetch tenants
  const { data: tenantsData } = useQuery({
    queryKey: ['tenants'],
    queryFn: async () => {
      const result = await client.models.Tenant.list();
      return result.data;
    },
  });
  const tenants: any[] = tenantsData || [];

  // Fetch standardized rules using auto-generated model list
  const { data: rulesData, isLoading: rulesLoading } = useQuery({
    queryKey: ['alertRules'],
    queryFn: async () => {
      const result = await client.models.AlertRule.list();
      return result.data || [];
    },
  });
  const rules: AlertRule[] = rulesData || [];

  // Fetch tenant's live rules
  const { data: tenantRulesData, isLoading: tenantRulesLoading } = useQuery({
    queryKey: ['tenantRules', selectedTenantId],
    queryFn: async () => {
      if (!selectedTenantId) return { rules: [] };
      const result = await client.queries.getTenantRules({ tenantId: selectedTenantId });
      const raw = result.data;
      return (typeof raw === 'string' ? JSON.parse(raw) : raw) || { rules: [] };
    },
    enabled: !!selectedTenantId && activeTab === 'tenant',
  });
  const tenantRules: TenantRule[] = tenantRulesData?.rules || [];

  // Create rule mutation using auto-generated model create
  const createRuleMutation = useMutation({
    mutationFn: async () => {
      setFormError('');
      let conditions: Record<string, unknown>;
      let actions: Record<string, unknown>;
      try {
        conditions = JSON.parse(form.conditions);
        actions = JSON.parse(form.actions);
      } catch {
        throw new Error('Conditions and Actions must be valid JSON');
      }
      const result = await client.models.AlertRule.create({
        name: form.name,
        description: form.description,
        ruleType: form.ruleType as any,
        severity: form.severity as any,
        conditions,
        actions,
        enabled: true,
        targetTenants: [],
      });
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['alertRules'] });
      setShowCreateForm(false);
      setForm(DEFAULT_FORM);
    },
    onError: (e: any) => setFormError(e.message),
  });

  // Delete rule mutation using auto-generated model delete
  const deleteRuleMutation = useMutation({
    mutationFn: async (ruleId: string) => {
      const result = await client.models.AlertRule.delete({ id: ruleId });
      return result.data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['alertRules'] }),
  });

  // Push rule mutation
  const pushRuleMutation = useMutation({
    mutationFn: async () => {
      if (!pushTargetRule || pushTenantIds.length === 0) return;
      const result = await client.mutations.pushAlertRule({
        ruleId: pushTargetRule.id,
        tenantIds: pushTenantIds,
      });
      const raw = result.data;
      return typeof raw === 'string' ? JSON.parse(raw) : raw;
    },
    onSuccess: (data) => {
      setPushResult(data);
    },
  });

  const togglePushTenant = (tenantId: string) => {
    setPushTenantIds((prev) =>
      prev.includes(tenantId) ? prev.filter((id) => id !== tenantId) : [...prev, tenantId],
    );
  };

  // Find which standard rules are missing from the selected tenant
  const tenantRuleNames = new Set(tenantRules.map((r) => r.ruleName?.toLowerCase()));
  const missingRules = rules.filter((r) => !tenantRuleNames.has(r.name.toLowerCase()));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Rule Management</h1>
          <p className="text-sm text-gray-500 mt-1">
            Define standardized IT security rules and detect drift across tenants.
          </p>
        </div>
        <button onClick={() => setShowCreateForm(true)} className="btn-primary">
          + New Rule
        </button>
      </div>

      {/* Tab switcher */}
      <div className="flex gap-1 bg-gray-100 dark:bg-gray-800 p-1 rounded-lg w-fit">
        {(['standard', 'tenant'] as const).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === tab
                ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
            }`}
          >
            {tab === 'standard' ? 'Standard Rules' : 'Tenant Comparison'}
          </button>
        ))}
      </div>

      {/* Standard Rules Tab */}
      {activeTab === 'standard' && (
        <div className="card overflow-hidden p-0">
          {rulesLoading ? (
            <div className="p-8 text-center text-gray-500">Loading rules...</div>
          ) : rules.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No standard rules defined yet. Create your first rule above.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-300">Name</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-300">Type</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-300">Severity</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600 dark:text-gray-300">Description</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {rules.map((rule) => (
                  <tr key={rule.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-4 py-3 font-medium text-gray-900 dark:text-white">{rule.name}</td>
                    <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                      {RULE_TYPE_LABELS[rule.ruleType || ''] || rule.ruleType}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${
                          SEVERITY_BADGE[rule.severity || 'LOW']
                        }`}
                      >
                        {rule.severity || 'LOW'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-500 text-xs">{rule.description || '—'}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2 justify-end">
                        <button
                          onClick={() => {
                            setPushTargetRule(rule);
                            setPushTenantIds([]);
                            setPushResult(null);
                          }}
                          className="text-blue-600 hover:text-blue-800 text-xs font-medium"
                        >
                          Push
                        </button>
                        <button
                          onClick={() => deleteRuleMutation.mutate(rule.id)}
                          disabled={deleteRuleMutation.isPending}
                          className="text-red-500 hover:text-red-700 text-xs font-medium"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Tenant Comparison Tab */}
      {activeTab === 'tenant' && (
        <div className="space-y-4">
          <div className="card">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Select Tenant
            </label>
            <select
              value={selectedTenantId}
              onChange={(e) => setSelectedTenantId(e.target.value)}
              className="input-field max-w-xs"
            >
              <option value="">Choose a tenant...</option>
              {tenants.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </select>
          </div>

          {selectedTenantId && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Standard Rules Column */}
              <div>
                <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-3">
                  Standard Rules ({rules.length})
                </h3>
                <div className="space-y-2">
                  {rules.map((rule) => {
                    const inTenant = tenantRuleNames.has(rule.name.toLowerCase());
                    return (
                      <div
                        key={rule.id}
                        className={`flex items-center justify-between p-3 rounded-lg border ${
                          inTenant
                            ? 'border-green-200 bg-green-50'
                            : 'border-orange-200 bg-orange-50'
                        }`}
                      >
                        <div>
                          <p className="text-sm font-medium text-gray-900">{rule.name}</p>
                          <p className="text-xs text-gray-500">{RULE_TYPE_LABELS[rule.ruleType || '']}</p>
                        </div>
                        <span className={`text-xs font-medium ${inTenant ? 'text-green-700' : 'text-orange-700'}`}>
                          {inTenant ? 'Present' : 'Missing'}
                        </span>
                      </div>
                    );
                  })}
                  {rules.length === 0 && (
                    <p className="text-sm text-gray-500">No standard rules defined.</p>
                  )}
                </div>
              </div>

              {/* Tenant Live Rules Column */}
              <div>
                <h3 className="text-base font-semibold text-gray-800 dark:text-gray-200 mb-3">
                  Live Tenant Rules{' '}
                  {!tenantRulesLoading && `(${tenantRules.length})`}
                </h3>
                {tenantRulesLoading ? (
                  <p className="text-sm text-gray-500">Loading from Google Workspace...</p>
                ) : tenantRulesData?.error ? (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                    <p className="text-sm text-red-700">
                      Could not fetch live rules: {tenantRulesData.error}
                    </p>
                    <p className="text-xs text-red-500 mt-1">
                      Ensure the <code>admin.reports.audit.readonly</code> scope is authorized for this tenant.
                    </p>
                  </div>
                ) : tenantRules.length === 0 ? (
                  <p className="text-sm text-gray-500">No rules found in this tenant.</p>
                ) : (
                  <div className="space-y-2">
                    {tenantRules.map((rule, i) => (
                      <div
                        key={i}
                        className="flex items-center justify-between p-3 rounded-lg border border-gray-200 bg-gray-50"
                      >
                        <div>
                          <p className="text-sm font-medium text-gray-900">{rule.ruleName || '(unnamed)'}</p>
                          <p className="text-xs text-gray-500">{rule.ruleType}</p>
                        </div>
                        {rule.severity && (
                          <span
                            className={`text-xs font-medium px-2 py-0.5 rounded ${SEVERITY_BADGE[rule.severity.toUpperCase()] || ''}`}
                          >
                            {rule.severity}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Drift Summary */}
          {selectedTenantId && !tenantRulesLoading && missingRules.length > 0 && (
            <div className="rounded-lg border border-orange-200 bg-orange-50 p-4">
              <p className="text-sm font-semibold text-orange-800 mb-2">
                Drift Detected — {missingRules.length} standard rule(s) not found in this tenant
              </p>
              <ul className="list-disc list-inside text-sm text-orange-700 space-y-1">
                {missingRules.map((r) => (
                  <li key={r.id}>{r.name}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Create Rule Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-lg">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Create Standard Rule
              </h3>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  className="input-field"
                  placeholder="e.g. Block External File Sharing"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                <input
                  type="text"
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  className="input-field"
                  placeholder="Optional description"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Type</label>
                  <select
                    value={form.ruleType}
                    onChange={(e) => setForm((f) => ({ ...f, ruleType: e.target.value }))}
                    className="input-field"
                  >
                    <option value="ACTIVITY_RULE">Activity Rule</option>
                    <option value="DLP_RULE">DLP Rule</option>
                    <option value="CUSTOM">Custom</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Severity</label>
                  <select
                    value={form.severity}
                    onChange={(e) => setForm((f) => ({ ...f, severity: e.target.value }))}
                    className="input-field"
                  >
                    <option value="CRITICAL">Critical</option>
                    <option value="HIGH">High</option>
                    <option value="MEDIUM">Medium</option>
                    <option value="LOW">Low</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Conditions (JSON)
                </label>
                <textarea
                  rows={3}
                  value={form.conditions}
                  onChange={(e) => setForm((f) => ({ ...f, conditions: e.target.value }))}
                  className="input-field font-mono text-xs"
                  placeholder='{ "triggerType": "DRIVE_SHARE_EXTERNAL" }'
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Actions (JSON)
                </label>
                <textarea
                  rows={3}
                  value={form.actions}
                  onChange={(e) => setForm((f) => ({ ...f, actions: e.target.value }))}
                  className="input-field font-mono text-xs"
                  placeholder='{ "alertAdmin": true, "blockAction": false }'
                />
              </div>

              {formError && (
                <p className="text-red-600 text-sm">{formError}</p>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => {
                    setShowCreateForm(false);
                    setForm(DEFAULT_FORM);
                    setFormError('');
                  }}
                  className="flex-1 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => createRuleMutation.mutate()}
                  disabled={!form.name || createRuleMutation.isPending}
                  className="flex-1 btn-primary disabled:opacity-50"
                >
                  {createRuleMutation.isPending ? 'Creating...' : 'Create Rule'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Push Rule Modal */}
      {pushTargetRule && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Push Rule: {pushTargetRule.name}
              </h3>
            </div>
            {pushResult ? (
              <div className="p-6 space-y-3">
                <p className="text-sm font-medium text-gray-700">Push Results</p>
                {(pushResult as any).results?.map((r: any) => (
                  <div
                    key={r.tenantId}
                    className={`flex items-center justify-between p-2 rounded border text-sm ${
                      r.success ? 'border-green-200 bg-green-50 text-green-800' : 'border-red-200 bg-red-50 text-red-800'
                    }`}
                  >
                    <span>{tenants.find((t) => t.id === r.tenantId)?.name || r.tenantId}</span>
                    <span>{r.success ? 'Success' : r.error}</span>
                  </div>
                ))}
                <button
                  onClick={() => {
                    setPushTargetRule(null);
                    setPushResult(null);
                    setPushTenantIds([]);
                  }}
                  className="w-full mt-2 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm hover:bg-gray-50"
                >
                  Close
                </button>
              </div>
            ) : (
              <div className="p-6 space-y-4">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Select tenants to push this rule to:
                </p>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {tenants.map((tenant) => (
                    <label key={tenant.id} className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={pushTenantIds.includes(tenant.id)}
                        onChange={() => togglePushTenant(tenant.id)}
                        className="rounded"
                      />
                      <span className="text-sm text-gray-800 dark:text-gray-200">{tenant.name}</span>
                      <span className="text-xs text-gray-400">{tenant.domain}</span>
                    </label>
                  ))}
                </div>
                <p className="text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 rounded p-2">
                  Note: Programmatic rule creation requires the{' '}
                  <code>admin.reports.audit.readonly</code> scope and super-admin delegation.
                  This action will be logged.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => {
                      setPushTargetRule(null);
                      setPushTenantIds([]);
                    }}
                    className="flex-1 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 text-sm hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => pushRuleMutation.mutate()}
                    disabled={pushTenantIds.length === 0 || pushRuleMutation.isPending}
                    className="flex-1 btn-primary disabled:opacity-50"
                  >
                    {pushRuleMutation.isPending ? 'Pushing...' : `Push to ${pushTenantIds.length} tenant(s)`}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
